import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { IntercomAPI } from 'react-intercom'
import { Button, Spinner, FormControl } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import MyInput from '../../../../features/forms/MyInput'
import { createSecret, updateSecret } from '../../../../features/aws/awsSecretsManagerSlice'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import {
  closeConnectionModal,
  postSlackMessage,
  fetchOAuthConnections,
  revokeOAuthConnection,
  selectOAuthConnections,
  selectOAuthConnectionsStatus,
} from '../../integrationsSlice'
import { toast } from 'react-toastify'
import { selectRootUser, selectRootAdmin } from '../../../../rootSlice'
import { getSecretId } from '../generic/helpers'
import MyCheckbox from '../../../../features/forms/MyCheckbox'
import MyLabel from '../../../../features/forms/MyLabel'
import {
  createOpenAIAccount,
  fetchOpenAIAccount,
  selectOpenAIAccount,
  selectOpenAIAccountStatus,
  updateOpenAIAccount,
} from '../../../../features/openai/openaiSlice'
import { SLACK_DATA_STATUS_CHANNEL_ID } from '../../../../modules/constants'
import { generateConnectorKey } from '../../utils'
import hosts from '../../../../modules/hosts.json'
import { createPublicKey, updatePublicKey } from '../../integrationsSlice'
import ReactTooltip from 'react-tooltip'
import { isTeamOwner as checkIsTeamOwner } from '../../isTeamOwner'

// Helper to get MCP host with fallback for dynamic staging
function getMcpHost(hostname: string): string {
  if (hosts.mcpHost[hostname]) {
    return hosts.mcpHost[hostname]
  }
  // Staging environments use the staging MCP host
  if (hostname.includes('staging')) {
    return 'https://mcp.staging.payhelm.com'
  }
  return hosts.mcpHost['app.payhelm.com']
}

export default function OpenAIConnectionModalContent({ ownerId, channel, retrieveAccounts, allPublicKeys }) {
  const dispatch = useDispatch()
  const closeModal = () => dispatch(closeConnectionModal())
  const user = useSelector(selectRootUser)
  const isAdmin = useSelector(selectRootAdmin)
  const isTeamOwner = checkIsTeamOwner(user)
  const { id: channelId, channel: channelName, ppKeys } = channel || {}
  const secretId = getSecretId(isTeamOwner ? user.id : user.ownerId, channelId)
  const targetConnectorKeyId = (isTeamOwner ? user.id : user.ownerId) + '--openai_connector'
  const connectorKey = allPublicKeys.find(({ keyId }) => keyId === targetConnectorKeyId)?.keyValue
  const mcpHost = getMcpHost(location.hostname)
  const mcpServerUrl = `${mcpHost}/sse`

  /**
   *
   * Fetch OAuth connections
   *
   */
  const oauthConnections = useSelector(selectOAuthConnections)
  const oauthConnectionsStatus = useSelector(selectOAuthConnectionsStatus)
  useEffect(() => {
    if (oauthConnectionsStatus === 'idle') {
      dispatch(fetchOAuthConnections())
    }
  }, [oauthConnectionsStatus, dispatch])

  const handleRevokeConnection = async (clientId: string) => {
    if (window.confirm('Are you sure you want to revoke this connection?')) {
      await dispatch(revokeOAuthConnection({ clientId }))
      toast.success('Connection revoked successfully')
    }
  }

  /**
   *
   * Fetch openai account
   *
   */
  const currentOpenAIAccount = useSelector(selectOpenAIAccount)
  const currentOpenAIAccountStatus = useSelector(selectOpenAIAccountStatus)
  useEffect(() => {
    if (currentOpenAIAccountStatus === 'idle') {
      dispatch(fetchOpenAIAccount({ key_id: secretId }))
    }
  }, [currentOpenAIAccountStatus])

  /**
   *
   * Toggle form type
   *
   */
  const [formType, setFormType] = useState('password')

  /**
   *
   * Handle form
   *
   */
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      privateKey: '',
      overview: true,
      products: true,
      marketing: true,
      geographics: true,
      customers: true,
      connectorKey: '',
    },
  })

  /**
   *
   * Set default form values
   *
   */

  const [privateKeyAvail, setPrivateKeyAvail] = useState(false)

  useEffect(() => {
    if (!ppKeys || _.isEmpty(ppKeys)) return

    const keyObj = ppKeys.find(({ keyId }) => keyId === secretId)
    if (keyObj) {
      setValue('privateKey', keyObj.privateKey)
      setPrivateKeyAvail(true)
    }
  }, [ppKeys])

  useEffect(() => {
    if (allPublicKeys.find(({ keyId }) => keyId === targetConnectorKeyId)) {
      setValue('connectorKey', allPublicKeys.find(({ keyId }) => keyId === targetConnectorKeyId)?.keyValue)
    }
  }, [allPublicKeys])

  /**
   *
   * Handle form submit
   *
   */
  const onSubmit = async (values) => {
    const { privateKey } = values
    const permissions = _.chain(values)
      .pick(['overview', 'products', 'marketing', 'geographics', 'customers'])
      .toPairs()
      .filter(([k, v]) => v === true)
      .fromPairs()
      .value()

    // Handle permissions
    try {
      if (currentOpenAIAccount?.id) {
        // If openai account already exists, update
        const updatePayload = {
          key_id: secretId,
          permissions,
        }

        await dispatch(updateOpenAIAccount(updatePayload))
      } else {
        // Otherwise, create
        const createPayload = {
          app_email: user.email,
          key_id: secretId,
          permissions,
        }

        await dispatch(createOpenAIAccount(createPayload))
      }
    } catch (err) {
      const errMessage = privateKey
        ? `Error while updating integration keys`
        : `Error while creating integration keys`
      console.error(errMessage, err)
      toast.error(errMessage)
    }

    // Handle key
    try {
      if (currentOpenAIAccount?.id) {
        // If secret already exists, update
        const updatePayload = {
          secretId,
          secretValue: privateKey,
        }

        await dispatch(updateSecret(updatePayload))
      } else {
        // Otherwise, create
        const createPayload = {
          secretId,
          secretValue: privateKey,
          description: `Private key for ${channelId}`,
          tags: [
            { Key: 'ownerId', Value: ownerId },
            { Key: 'channelId', Value: channelId },
          ],
        }

        await dispatch(createSecret(createPayload))
      }
      await retrieveAccounts()
    } catch (err) {
      const errMessage = privateKey
        ? `Error while updating integration keys`
        : `Error while creating integration keys`
      console.error(errMessage, err)
      toast.error(errMessage)
    }

    try {
      // Handle connector key
      if (connectorKey) {
        const updatePayload = {
          keyId: targetConnectorKeyId,
          keyValue: getValues('connectorKey'),
        }

        await dispatch(updatePublicKey(updatePayload))
      } else {
        const createPayload = {
          keyId: targetConnectorKeyId,
          keyValue: getValues('connectorKey'),
        }

        await dispatch(createPublicKey(createPayload))
      }
    } catch (err) {
      const errMessage = `Error while updating integration keys`
      console.error(errMessage, err)
      toast.error(errMessage)
    }

    // Send message to a slack channel for notification
    dispatch(
      postSlackMessage({
        channel: SLACK_DATA_STATUS_CHANNEL_ID,
        text: `${user.email} saved api keys for ${channelName} integration`,
      })
    )

    // Track it via intercom
    IntercomAPI('trackEvent', 'clicked-integration-connect-button', {
      email: user.email,
      channel: channelName,
    })

    const successMessage = privateKey
      ? `Successfully updated ${channelName} keys`
      : `Successfully created ${channelName} keys`
    toast.success(successMessage)
    closeModal()
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-2">Connect Your {channelName} Account</h2>
      <div className="tw-text-slate-500 tw-mb-4">
        Please enter your {channelName} API key below to connect your account.
      </div>

      {!ppKeys ? (
        <Spinner animation="border" variant="primary" />
      ) : (
        <form className="tw-w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="tw-w-full tw-flex tw-items-center tw-space-x-2 tw-mb-3">
            <MyInput
              register={register}
              name="privateKey"
              label="Private key"
              type={formType}
              rules={{ required: 'Private key is required' }}
              errors={errors}
              disabled={!isTeamOwner}
              placeholder={isTeamOwner ? 'Private key' : 'Only team owner can edit private key'}
            />

            {!privateKeyAvail ? formType === 'password' ? (
              <div
                className="tw-shrink-0 tw-mt-6 tw-cursor-pointer tw-text-slate-600 hover:tw-text-slate-900 tw-text-lg"
                onClick={() => setFormType('text')}
              >
                <FiEye />
              </div>
            ) : (
              <div
                className="tw-shrink-0 tw-mt-6 tw-cursor-pointer tw-text-slate-600 hover:tw-text-slate-900 tw-text-lg"
                onClick={() => setFormType('password')}
              >
                <FiEyeOff />
              </div>
            ) : null}
          </div>

          <div className="tw-block tw-mb-1 tw-text-sm tw-font-medium tw-text-gray-500">Data access permissions</div>
          <div className="tw-w-full tw-flex tw-flex-wrap tw-mb-6">
            <div className="tw-mr-2 tw-mb-2">
              <MyCheckbox register={register} name="overview" label="Overview" disabled={true} />
            </div>
            <div className="tw-mr-2 tw-mb-2">
              <MyCheckbox register={register} name="products" label="Products" />
            </div>
            <div className="tw-mr-2 tw-mb-2">
              <MyCheckbox register={register} name="marketing" label="Marketing" />
            </div>
            <div className="tw-mr-2 tw-mb-2">
              <MyCheckbox register={register} name="geographics" label="Geographics" />
            </div>
            <div className="tw-mr-2 tw-mb-2">
              <MyCheckbox register={register} name="customers" label="Customers" />
            </div>
          </div>

          {/* Connector Key Section (for non-OAuth / legacy flow) */}
          {isTeamOwner && (
            <div className="tw-mb-6">
              <MyInput
                register={register}
                name="connectorKey"
                label="Connector key (for direct MCP access)"
                labelSuffix={
                  <>
                    <i className="fa fa-info-circle ml1" data-tip data-for={`connector-key-tooltip`} />
                    <ReactTooltip isMultiline id={`connector-key-tooltip`}>
                      <div style={{ maxWidth: 240, textAlign: 'left' }}>
                        Use this key for direct MCP access without OAuth. Copy the MCP Server URL to configure your AI assistant manually.
                      </div>
                    </ReactTooltip>
                  </>
                }
                type="text"
                disabled={true}
              />
              <div className='right'>
                <Button
                  variant="secondary"
                  className="tw-mt-2 tw-mr-2"
                  onClick={() => {
                    // copy connector key to clipboard
                    navigator.clipboard.writeText(mcpServerUrl + `?key=${getValues('connectorKey')}`)
                    toast.success('MCP Server URL copied to clipboard')
                  }}
                  disabled={!getValues('connectorKey')}
                >
                  Copy MCP Server URL
                </Button>
                <Button
                  className="tw-mt-2"
                  variant="primary"
                  onClick={() => {
                    // generate connector key
                    const newConnectorKey = generateConnectorKey()
                    setValue('connectorKey', newConnectorKey)
                  }}
                >
                  {`${connectorKey ? 'Regenerate' : 'Generate'} Connector Key`}
                </Button>
              </div>
            </div>
          )}

          {/* OAuth Connections Section */}
          <div className="tw-mb-6">
            <div className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-500">
              Connected AI Assistants (OAuth)
              <i className="fa fa-info-circle ml1" data-tip data-for="oauth-connections-tooltip" />
              <ReactTooltip isMultiline id="oauth-connections-tooltip">
                <div style={{ maxWidth: 240, textAlign: 'left' }}>
                  AI assistants like ChatGPT can connect via OAuth for secure, authenticated access to your data.
                </div>
              </ReactTooltip>
            </div>
            {oauthConnectionsStatus === 'loading' && (
              <div className="tw-text-sm tw-text-gray-400">Loading connections...</div>
            )}
            {oauthConnectionsStatus === 'succeeded' && oauthConnections.length === 0 && (
              <div className="tw-text-sm tw-text-gray-400">No AI assistants connected via OAuth yet.</div>
            )}
            {oauthConnectionsStatus === 'succeeded' && oauthConnections.length > 0 && (
              <div className="tw-space-y-2">
                {oauthConnections.map((conn) => (
                  <div
                    key={conn.client_id}
                    className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200"
                  >
                    <div>
                      <div className="tw-font-medium tw-text-gray-900">{conn.client_name}</div>
                      <div className="tw-text-xs tw-text-gray-500">
                        Connected {new Date(conn.connected_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-green-700 tw-bg-green-100 tw-rounded">
                        Connected
                      </span>
                      {isTeamOwner && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRevokeConnection(conn.client_id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr />

          <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !isTeamOwner}>
              Save {channelName} Connection
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
